import * as React from 'react';
import { find } from 'lodash';
// # interfaces
import { iPermission, iPermissionKey, iPermissionWithWarning } from '@/interfaces/permissions';
import * as _ from 'lodash';

// # components
import { Select } from '@/components/Select';
import { List } from '@/components/List';
import { Message } from '@/components/feedback';

export interface iProps {
    permissions: iPermission[];
    value: iPermissionKey[];
    onChange: (keys: iPermissionKey[]) => any;
}

interface iGroup<iItem> {
    title: string;
    items: Array<iItem>;
}

/**
 * Renders a dropdown of permissions, and a list of selected items.
 *
 * @type {React.SFC}
 */
const SelectPermissions: React.SFC<iProps> = (props: iProps) => {
    /**
     * Finds the permissions that match the keys from props.value
     *
     * @returns {iPermission[]}
     */
    const getSelectedItems = (): iPermission[] => {
        return props.value.map(key => {
            const original = find(props.permissions, { key });

            if (!original) {
                return {
                    default_roles: [],
                    feature: 'Missing',
                    key: 'UNKNOWN_PERMISSION',
                    label: '',
                    warning: `"${key}" permission could not be found. It may no longer be supported.`,
                } as iPermission;
            }

            return original;
        });
    };

    /**
     * Adds a selected permission and calls onChange prop.
     *
     * @param {iPermission} permission
     * @returns {void}
     */
    const handleSelectPermission = (permission: iPermission): void => {
        const selected = props.value.includes(permission.key);
        if (selected) { return; }
        props.onChange([
            ...props.value,
            permission.key,
        ]);
    };

    const handleGroupHeaderClick = (feature: string) => {
        const groupPermissions = props.permissions.filter(permission => permission.feature === feature);
        const notSelected = groupPermissions.filter(permission => !props.value.includes(permission.key));
        const valueCopy = [...props.value];
        notSelected.forEach(permission => {
            valueCopy.push(permission.key);
        });
        props.onChange(valueCopy);
    };

    const areAllPermissionSelectedInGroup = (feature: string) => {
        const groupPermissions = props.permissions.filter(permission => permission.feature === feature);
        return groupPermissions.every(permission => props.value.includes(permission.key));
    };

    /**
     * Removes a selected permission and calls onChange prop.
     *
     * @param permission
     */
    const handleRemovePermission = (permission: iPermission): void => {
        const selected = props.value.includes(permission.key);
        if (!selected) { return; }
        const valueCopy = [...props.value];
        valueCopy.splice(valueCopy.indexOf(permission.key), 1);
        props.onChange(valueCopy);
    };

    /**
     * Renders a single item in the list of selected permissions.
     *
     * @param {iPermissionWithWarning} permission
     * @returns {React.ReactNode}
     */
    const renderPermissionListItem = (permission: iPermissionWithWarning): React.ReactElement => {
        if (!permission) {
            return null;
        }

        return (
            <div className="list__item permissions-list__item" key={permission.key}>
                <div>
                    {permission.label}
                    {permission.warning ? (
                        <Message theme="warning">{permission.warning}</Message>
                    ) : null}
                    <span className="permissions-list__item-key">{permission.key}</span>
                </div>
                <button
                    className="permissions-list__remove-item-button"
                    onClick={() => handleRemovePermission(permission)}
                    title="Remove permission"
                >
                    <i className="fa fa-trash" />
                </button>
            </div>
        );
    };

    /**
     * Renders a single item in the permission select dropdown.
     * We're specifying a custom renderer for this so we can disable items that have already been selected.
     *
     * @param {iPermission} permission
     * @param {() => any} clickHandler
     * @returns {React.ReactNode}
     */
    const renderPermissionSelectItem = (permission: iPermission, clickHandler: () => any): React.ReactElement => {
        const isSelected = props.value.includes(permission.key);

        // intercept click handler to disable clicks on selected items
        const handleClick = () => {
            if (!isSelected) {
                clickHandler();
            }
        };

        const classNames = [
            'list__item',
            'select-permissions__item',
            isSelected ? 'is-disabled' : 'is-interactive',
        ];

        return (
            <div className={classNames.join(' ')} key={permission.key} onClick={handleClick}>
                {permission.label} {isSelected ? '(already added)' : ''}
                <span className="select-permissions__item-key">{permission.key}</span>
            </div>
        );
    };

    const RenderGroupHeader = (group: iGroup<any>, onGroupHeaderClick: (feature: string)=>void) => {
        if (group.title === List.ungroupedGroupTitle) {
            return null;
        }

        return (
            <div className="list__group-header is-interactive" style={{ display: 'flex', gap: '20px' }}>
                <h4 className="list__group-title is-interactive" >
                    {_.capitalize(_.get(group, 'title', '[Group Title]'))} ({group.items.length.toString()})
                </h4>
                {areAllPermissionSelectedInGroup(group.title) ? <></> : <input type="checkbox" onClick={()=>onGroupHeaderClick(group.title)}/>}
            </div>
        );
    };

    return (
        <div className="select-permissions">
            <Select<iPermission>
                className="select-permissions"
                groupBy="feature"
                items={props.permissions}
                onSelect={handleSelectPermission}
                renderItem={renderPermissionSelectItem}
                onGroupHeaderClick={(feature: string)=>{
                    handleGroupHeaderClick(feature);
                }}
                renderGroupHeader={RenderGroupHeader}
            />
            <List<iPermissionWithWarning>
                className="permissions-list"
                groupBy="feature"
                items={getSelectedItems()}
                renderItem={renderPermissionListItem}
                renderEmptyPlaceholder={() => (
                    <Message theme="warning">
                        No permissions selected. Choose one from the dropdown above.
                    </Message>
                )}
            />
        </div>
    );
};

export default SelectPermissions;
